import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Collapse, message, Modal, Spin, Table, Tabs } from 'antd';
import TagBadge from '../../../views/tags/components/TagBadge';
import squareCatalogApi from '../../../services/squareCatalogApi';
import squareErrorToast from './squareErrorToast';
import usePosMappingAccess from '../pos-mapping/use-pos-mapping-access';

const SquareReportsModal = ({ open, onClose }) => {
  const globalCompany = useSelector((state) => state.companyReducer.selected);
  const { canEdit: canManagePriceSource } = usePosMappingAccess();
  const [activeTab, setActiveTab] = useState('price');

  const [priceReportLoading, setPriceReportLoading] = useState(false);
  const [priceReportRows, setPriceReportRows] = useState([]);

  const [modifierReportLoading, setModifierReportLoading] = useState(false);
  const [modifierReport, setModifierReport] = useState(null);

  const [resolvingKey, setResolvingKey] = useState(null);

  useEffect(() => {
    if (!open || !globalCompany?.id) return undefined;
    let cancelled = false;
    (async () => {
      if (activeTab === 'price') {
        setPriceReportLoading(true);
        try {
          const res = await squareCatalogApi.priceMismatches(globalCompany.id);
          if (!cancelled) setPriceReportRows(res.data.data || []);
        } catch (err) {
          if (!cancelled) {
            squareErrorToast(err);
            onClose();
          }
        } finally {
          if (!cancelled) setPriceReportLoading(false);
        }
      } else if (activeTab === 'modifiers') {
        setModifierReportLoading(true);
        try {
          const res = await squareCatalogApi.modifierMappingReview(
            globalCompany.id
          );
          if (!cancelled) setModifierReport(res.data.data || null);
        } catch (err) {
          if (!cancelled) {
            squareErrorToast(err);
            onClose();
          }
        } finally {
          if (!cancelled) setModifierReportLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, activeTab, globalCompany?.id]);

  const resolvePrice = async (row, action) => {
    try {
      setResolvingKey(row.itemid);
      const res = await squareCatalogApi.priceMismatchResolve({
        companyid: globalCompany.id,
        itemid: row.itemid,
        action,
      });
      const newprice = res.data.data?.newprice;
      setPriceReportRows((prev) => prev.filter((r) => r.itemid !== row.itemid));
      message.success(
        action === 'adoptPrice'
          ? newprice != null
            ? `Price updated to $${Number(newprice).toFixed(2)}`
            : 'Price updated'
          : 'Square now manages this price'
      );
    } catch (err) {
      squareErrorToast(err);
    } finally {
      setResolvingKey(null);
    }
  };

  const importGroup = async (row) => {
    try {
      setResolvingKey(row.squareid);
      await squareCatalogApi.modifierGroupImport({
        companyid: globalCompany.id,
        squaremodifierlistid: row.squareid,
      });
      setModifierReport((prev) =>
        prev
          ? { ...prev, squareonly: prev.squareonly.filter((g) => g.squareid !== row.squareid) }
          : prev
      );
      message.success('Group imported — attach it to products from the Modifiers screen.');
    } catch (err) {
      squareErrorToast(err);
    } finally {
      setResolvingKey(null);
    }
  };

  const priceTabContent = priceReportLoading ? (
    <div className="flex justify-center py-8">
      <Spin />
    </div>
  ) : priceReportRows.length === 0 ? (
    <p className="py-4 text-sm text-gray-600 dark:text-slate-300">
      No price mismatches — all custom-priced items match Square.
    </p>
  ) : (
    <Table
      size="small"
      pagination={false}
      rowKey="itemid"
      dataSource={priceReportRows}
      columns={[
        { title: 'Menu', dataIndex: 'menuname' },
        { title: 'Product', dataIndex: 'productname' },
        { title: 'Item', dataIndex: 'itemname' },
        { title: 'Square name', dataIndex: 'squarename' },
        {
          title: 'Thisway price',
          dataIndex: 'thiswayprice',
          render: (v) => `$${Number(v).toFixed(2)}`,
        },
        {
          title: 'Square price',
          dataIndex: 'squareprice',
          render: (v) => `$${Number(v).toFixed(2)}`,
        },
        {
          title: '',
          dataIndex: 'itemid',
          render: (itemid, row) => (
            <span className="flex gap-2">
              <button
                type="button"
                disabled={!!resolvingKey}
                onClick={() => resolvePrice(row, 'adoptPrice')}
                className="rounded-md border border-slate-200 px-2 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-600"
              >
                Adopt price
              </button>
              {canManagePriceSource && (
                <button
                  type="button"
                  disabled={!!resolvingKey}
                  onClick={() => resolvePrice(row, 'useSquarePrice')}
                  className="rounded-md border border-slate-200 px-2 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-600"
                >
                  Let Square manage
                </button>
              )}
            </span>
          ),
        },
      ]}
    />
  );

  const modifierTabContent = modifierReportLoading ? (
    <div className="flex justify-center py-8">
      <Spin />
    </div>
  ) : !modifierReport ||
    (modifierReport.mappedgroups.length === 0 &&
      modifierReport.thiswayonly.length === 0 &&
      modifierReport.squareonly.length === 0) ? (
    <p className="py-4 text-sm text-gray-600 dark:text-slate-300">
      No modifier groups found.
    </p>
  ) : (
    <div className="space-y-4">
      {modifierReport.mappedgroups.length > 0 && (
        <div>
          <h4 className="mb-2 text-sm font-semibold text-gray-700 dark:text-slate-200">
            Mapped groups
          </h4>
          <Collapse
            size="small"
            items={modifierReport.mappedgroups.map((g, i) => ({
              key: String(i),
              label: (
                <span className="text-sm">
                  {g.name}
                  {g.squarename && g.squarename !== g.name && (
                    <span className="ml-1 text-xs text-gray-400">
                      Square: {g.squarename}
                    </span>
                  )}
                  {!g.squarefound && (
                    <TagBadge
                      className="ml-1"
                      text="Not in Square"
                      textColor="white"
                      color="red"
                      size="xs"
                    />
                  )}
                </span>
              ),
              children: (
                <ul className="space-y-1">
                  {g.options.map((o, j) => (
                    <li
                      key={j}
                      className="flex items-center gap-2 text-sm text-gray-700 dark:text-slate-300"
                    >
                      <span>{o.name}</span>
                      {o.squarename && o.squarename !== o.name && (
                        <span className="text-xs text-gray-400">
                          Square: {o.squarename}
                        </span>
                      )}
                      {!o.mapped && (
                        <TagBadge
                          text="Custom"
                          textColor="white"
                          color="#d97706"
                          size="xs"
                        />
                      )}
                      {o.squaremissing && (
                        <TagBadge
                          text="Missing in Square"
                          textColor="white"
                          color="red"
                          size="xs"
                        />
                      )}
                      <span className="ml-auto text-xs">
                        ${Number(o.price).toFixed(2)}
                        {o.squareprice != null &&
                          Number(o.squareprice) !== Number(o.price) && (
                            <> → ${Number(o.squareprice).toFixed(2)}</>
                          )}
                      </span>
                    </li>
                  ))}
                </ul>
              ),
            }))}
          />
        </div>
      )}
      {modifierReport.thiswayonly.length > 0 && (
        <div>
          <h4 className="mb-2 text-sm font-semibold text-gray-700 dark:text-slate-200">
            Thisway-only groups
          </h4>
          <ul className="space-y-0.5 text-sm text-gray-700 dark:text-slate-300">
            {modifierReport.thiswayonly.map((g, i) => (
              <li key={i}>
                {g.name}{' '}
                <span className="text-xs text-gray-400">
                  ({g.optioncount} options)
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {modifierReport.squareonly.length > 0 && (
        <div>
          <h4 className="mb-2 text-sm font-semibold text-gray-700 dark:text-slate-200">
            New in Square (not linked)
          </h4>
          <ul className="space-y-0.5 text-sm text-gray-700 dark:text-slate-300">
            {modifierReport.squareonly.map((l, i) => (
              <li key={l.squareid || i}>
                {l.name}{' '}
                <span className="text-xs text-gray-400">
                  ({l.modifiercount} modifiers)
                </span>
                <button
                  type="button"
                  disabled={!!resolvingKey}
                  onClick={() => importGroup(l)}
                  className="ml-2 rounded-md bg-blue-600 px-2 py-1 text-xs font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  Import group
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );

  return (
    <Modal
      title="Square reports"
      open={open}
      onCancel={onClose}
      footer={null}
      width={760}
    >
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          { key: 'price', label: 'Price', children: priceTabContent },
          {
            key: 'modifiers',
            label: 'Modifiers',
            children: modifierTabContent,
          },
        ]}
      />
    </Modal>
  );
};

export default SquareReportsModal;
